
import { Edit2, Trash2, Phone, Mail, MapPin } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { OptimizedImage } from "@/components/ui/optimized-image";

interface Service {
  id: string;
  service_name: string;
  description: string;
  duration: number;
  price: number;
  address: string;
  phone: string;
  email: string;
  photo_url: string;
  country: string;
  category?: string;
  keywords?: string[];
  currency?: string;
}

interface ServiceCardProps {
  service: Service;
  onEdit: (service: Service) => void;
  onDelete: (service: Service) => void;
}

export const ServiceCard = ({ service, onEdit, onDelete }: ServiceCardProps) => {
  const getCurrencySymbol = (currencyCode: string = 'EUR') => {
    switch (currencyCode) {
      case "EUR": return "€";
      case "USD": return "$";
      case "HTG": return "G";
      case "DOP": return "RD$";
      case "GBP": return "£";
      case "CAD": return "CA$";
      case "CHF": return "Fr";
      case "AUD": return "A$";
      case "JPY": return "¥";
      case "CNY": return "¥";
      case "RUB": return "₽";
      case "BRL": return "R$";
      case "MXN": return "Mex$";
      case "INR": return "₹";
      case "MAD": return "DH";
      case "XAF": return "FCFA";
      case "XOF": return "CFA";
      case "NGN": return "₦";
      case "ZAR": return "R";
      case "KES": return "KSh";
      default: return currencyCode;
    }
  };

  return (
    <Card key={service.id}>
      <CardHeader>
        <CardTitle className="flex items-start justify-between">
          <span>{service.service_name}</span>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onEdit(service)}
            >
              <Edit2 className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onDelete(service)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {service.photo_url && (
          <div className="relative aspect-video w-full overflow-hidden rounded-lg">
            <OptimizedImage
              src={service.photo_url}
              alt={service.service_name}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        )}
        <p className="text-sm text-muted-foreground">
          {service.description || "Aucune description"}
        </p>
        <div className="flex justify-between text-sm">
          <span>Durée: {service.duration} min</span>
          <span>Prix: {service.price}{getCurrencySymbol(service.currency)}</span>
        </div>
        <div className="space-y-2 text-sm text-muted-foreground">
          {service.address && (
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              <span>{service.address}</span>
            </div>
          )}
          {service.country && (
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              <span>{service.country}</span>
            </div>
          )}
          {service.phone && (
            <div className="flex items-center gap-2">
              <Phone className="h-4 w-4" />
              <span>{service.phone}</span>
            </div>
          )}
          {service.email && (
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              <span>{service.email}</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
